/**
 * Client for the enterprise directory API (services/firewall/src/routes/directoryRoutes.ts).
 * On-prem Active Directory over LDAP/LDAPS: connection settings, sync, and the
 * synced users/groups the policy pickers offer.
 */

export interface DirectoryConfig {
  orgId: string;
  provider: 'active_directory';
  url: string;
  bindDn: string;
  hasPassword: boolean;
  baseDn: string;
  userFilter: string | null;
  groupFilter: string | null;
  netbiosDomain: string | null;
  caPem: string | null;
  tlsVerify: boolean;
  startTls: boolean;
  syncIntervalMin: number;
  enabled: boolean;
  lastSyncAt: number | null;
  lastSyncStatus: string | null;
  lastSyncCounts: { users: number; groups: number; memberships: number } | null;
  updatedAt?: string;
  updatedBy?: string | null;
}

export interface DirectoryConfigInput {
  url: string;
  bindDn: string;
  /** Leave empty to keep the stored password. */
  bindPassword?: string;
  baseDn: string;
  userFilter?: string | null;
  groupFilter?: string | null;
  netbiosDomain?: string | null;
  caPem?: string | null;
  tlsVerify?: boolean;
  startTls?: boolean;
  syncIntervalMin?: number;
  enabled?: boolean;
}

export interface DirectoryUser {
  sid: string;
  upn: string | null;
  sam: string | null;
  netbiosDomain: string | null;
  displayName: string | null;
  mail: string | null;
  disabled: boolean;
}

export interface DirectoryGroup {
  sid: string;
  name: string;
  dn: string | null;
  description: string | null;
  memberCount?: number;
}

/** Target-policy subject for an AD group. */
export const groupSubject = (sid: string) => `group:${sid}`;
export const isGroupSubject = (subject: string) => subject.startsWith('group:');

/** Error carrying the server's hint (bad credentials, untrusted cert, unreachable DC). */
export class DirectoryError extends Error {
  constructor(message: string, public hint?: string) {
    super(message);
  }
}

async function asJson(res: Response): Promise<any> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new DirectoryError(data.message || data.error || `Request failed (${res.status})`, data.hint);
  return data;
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export async function getConfig(apiUrl: string): Promise<DirectoryConfig | null> {
  return (await asJson(await fetch(`${apiUrl}/admin/directory/config`))).config;
}

export async function saveConfig(apiUrl: string, input: DirectoryConfigInput): Promise<DirectoryConfig> {
  return (await asJson(await fetch(`${apiUrl}/admin/directory/config`, json('PUT', input)))).config;
}

export async function testConnection(
  apiUrl: string,
  input?: DirectoryConfigInput
): Promise<{ ok: true; netbiosDomain: string | null; sampleUsers: number; sampleGroups: number }> {
  return asJson(await fetch(`${apiUrl}/admin/directory/test`, json('POST', input || {})));
}

export async function syncNow(
  apiUrl: string
): Promise<{ result: { users: number; groups: number; memberships: number; durationMs: number }; config: DirectoryConfig }> {
  return asJson(await fetch(`${apiUrl}/admin/directory/sync`, json('POST', {})));
}

export async function listUsers(apiUrl: string, q = '', limit = 50): Promise<DirectoryUser[]> {
  const qs = new URLSearchParams({ q, limit: String(limit) });
  return (await asJson(await fetch(`${apiUrl}/admin/directory/users?${qs}`))).users;
}

export async function listGroups(apiUrl: string, q = '', limit = 50): Promise<DirectoryGroup[]> {
  const qs = new URLSearchParams({ q, limit: String(limit) });
  return (await asJson(await fetch(`${apiUrl}/admin/directory/groups?${qs}`))).groups;
}

export async function groupMembers(apiUrl: string, sid: string): Promise<DirectoryUser[]> {
  return (await asJson(await fetch(`${apiUrl}/admin/directory/groups/${encodeURIComponent(sid)}/members`))).members;
}
